import { Suspense, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { BrowserRouter } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import RouteList from 'routers'
import firebaseInit from 'utils/firebaseInit'
import CustomerAPI from 'api/customers'
import * as utilsCommon from 'utils/booking/common'
import StorageKeys from 'constants/storage-keys'
import { checkToShowLoadingForNewBooking } from './utils/new_booking/common'
import { Utils } from 'utils/Utils'
import { crossStorageUtils } from './utils/crossStorage'
import * as areaActionCreators from 'store/actions/common/areaActionCreators'
import * as customerActionCreators from 'store/actions/common/customerActionCreators'
import jwt_decode from 'jwt-decode'
import NavBarContainer from './containers/NavBarContainer'
import FreshChat from 'utils/FreshChat'
import { initBasicModal } from 'assets/javascripts/webapp-v2/common/basic-modal'
import LoadingModal from 'components/common/loading_modal/LoadingModal'
import { ThemeProvider } from 'styled-components'
import { SGlobal } from 'SGlobal'
import { theme } from 'components/common/Styled/STheme'
import { COMPANY_APPROVED_MODAL } from 'components/new_booking/guest_flow/constants'
import { accountManageActions } from 'store/toolkit/accountManage/accountManage.reducer'
import accountAction from 'store/actions/common/accountAction'
import { appsFlyerSetCUID, appsFlyerTrackEvent, appsFlyerTurnOnDevMode } from 'utils/trackingAppsFlyer'
import { SIGN_OUT } from 'constants/trackingAppsFlyer'
import CommonUtils from 'utils/common'
import pinpointService from 'utils/pinpointService'
import { initApp } from 'utils/initApp'
import { VISITOR_ID } from 'utils/visitorIdManager'

const currentToken = (isLoggedOut) => (isLoggedOut ? '' : window.localStorage.getItem(StorageKeys.ACCESS_TOKEN) || '')

const handleLogoutNotify = (isLoggedOut, t) => {
  if (isLoggedOut) {
    Utils.showToastrMessage('info', t('devise.sessions.signed_out'))
    crossStorageUtils.setAccessToken('')
    appsFlyerTrackEvent(SIGN_OUT)
    appsFlyerSetCUID('')
    window.history.replaceState({}, document.title, '/')
  }
}

const App = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const currentCustomer = useSelector((state) => state.currentCustomer)
  const urlParams = new URLSearchParams(window.location.search)
  const isLoggedOut = urlParams.get('log_out_success')
  const [isInnitCrossStorageSuccess, setIsInnitCrossStorageSuccess] = useState(false)

  const areaIdUrl = utilsCommon.getParamFromURL('area_id')
  if (areaIdUrl) window.localStorage.setItem(StorageKeys.AREA_ID, areaIdUrl)
  const areaIdLocal = window.localStorage.getItem(StorageKeys.AREA_ID)
  const extraInfos = useSelector((state) => state.extraInfos)
  const finalAreaId = Number(extraInfos?.id || areaIdUrl || areaIdLocal)
  const accessToken = currentToken(isLoggedOut)
  let companyIdFromToken = null
  if (accessToken) {
    const decodeToken = jwt_decode(accessToken)
    companyIdFromToken = +decodeToken?.company_id || null
  }

  const isShowHeaderAndChat = initApp.isShowHeaderAndChat()

  const isReady =
    (extraInfos?.id && (!accessToken || currentCustomer.id)) ||
    !isShowHeaderAndChat ||
    CommonUtils.isExportOrDownloadAnalyticsLater()
  useEffect(() => {
    initApp.checkParamLCL()
    initBasicModal()
    handleLogoutNotify(isLoggedOut, t)
    crossStorageUtils.connectToken(accessToken, setIsInnitCrossStorageSuccess)
    appsFlyerTurnOnDevMode()
    checkToShowLoadingForNewBooking()
    pinpointService.checkTimeToSendEvent()
    pinpointService.getServerTimeOffset()

    localStorage.removeItem(StorageKeys.SWITCH_ACCOUNT_ATTACHMENTS)
    sessionStorage.removeItem(VISITOR_ID) 
  }, [])

  useEffect(() => {
    if (!isInnitCrossStorageSuccess) return
    window.addEventListener('message', crossStorageUtils.handleListenMessage)
    return () => {
      window.removeEventListener('message', crossStorageUtils.handleListenMessage)
    }
  }, [isInnitCrossStorageSuccess])

  useEffect(() => {
    const checkEventApprovedCompany = async () => {
      const { data: checkEvent } = await CustomerAPI.checkEventApprovedCompany()
      if (checkEvent?.show_notification) {
        await accountAction.switchAccount(checkEvent?.value?.[0])(dispatch)
        dispatch(accountManageActions.updateModalAccountManage(COMPANY_APPROVED_MODAL))
        await CustomerAPI.cleanEventApprovedCompany()
      }
    }
    if (currentCustomer?.id && isInnitCrossStorageSuccess) {
      checkEventApprovedCompany()
      firebaseInit.load()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentCustomer?.id, isInnitCrossStorageSuccess])

  useEffect(() => {
    dispatch(customerActionCreators.getCurrentCustomer(accessToken, finalAreaId, companyIdFromToken))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken, finalAreaId, companyIdFromToken])

  useEffect(() => {
    dispatch(customerActionCreators.getCustomerSettings(finalAreaId, companyIdFromToken))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [finalAreaId, extraInfos.country_code, currentCustomer.id, companyIdFromToken])

  useEffect(() => {
    dispatch(areaActionCreators.getAreaSetting(accessToken, currentCustomer, finalAreaId))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [finalAreaId, companyIdFromToken, currentCustomer.id])

  useEffect(() => {
    initApp.checkToRedirectToLCLTracking(currentCustomer, accessToken)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentCustomer.id, companyIdFromToken, currentCustomer.authentication_token])

  //default show loading, we need hide loading for each pages
  return (
    <ThemeProvider theme={theme}>
      <div className="app">
        <Suspense fallback={<LoadingModal />}>
          <BrowserRouter>
            <SGlobal />
            {isReady && (
              <>
                {isShowHeaderAndChat && (
                  <>
                    <FreshChat />
                    <NavBarContainer />
                  </>
                )}
                <RouteList />
              </>
            )}
            <LoadingModal />
          </BrowserRouter>
        </Suspense>
      </div>
    </ThemeProvider>
  )
}

export default App
